import { useEffect, useState, useCallback } from 'react';
import { Plus, MapPin, Edit, Trash2, Shield, AlertTriangle, Check, Layout, ChefHat, BookOpen, Calendar, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { LocationFormDialog } from './LocationFormDialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';

export type Location = {
  id: string;
  tenantId: string;
  tenantName?: string;
  name: string;
  address: string | null;
  city: string | null;
  posMode: 'store' | 'restaurant';
  storeLayout: 'modern' | 'retro';
  restrictPayments: boolean;
  stockMode: 'strict' | 'warn' | 'lenient';
  createdAt: string;
  salesCount?: number;
  movementCount?: number;
  hasCertificate?: boolean;
  certExpiresAt?: string | null;
};

const STOCK_MODE_LABELS: Record<string, { label: string; color: string }> = {
  strict: { label: 'Strict (block prodaje u minus)', color: '#10b981' },
  warn: { label: 'Warn (dozvoli + log)', color: '#f59e0b' },
  lenient: { label: 'Lenient (uvek dozvoli)', color: '#6b7280' },
};

export function LocationsPage() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Location | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Location | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.get<Location[]>('/admin/locations');
      setLocations(rows);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/locations/${deleteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Lokacije</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Upravljanje lokacijama tenant-a, POS režimima i magacin politikama
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}>
          <Plus size={14} /> Nova lokacija
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div style={{
        background: 'var(--color-bg-surface)',
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid var(--color-border)',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Tenant</th>
                <th style={th}>Naziv</th>
                <th style={th}>Grad</th>
                <th style={th}>POS režim</th>
                <th style={th}>Stock mod</th>
                <th style={th}>Cert</th>
                <th style={{ ...th, textAlign: 'right' }}>Sales</th>
                <th style={{ ...th, textAlign: 'right' }}>Movement-i</th>
                <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
              </tr>
            </thead>
            <tbody>
              {!loading && locations.length === 0 && (
                <tr><td colSpan={9} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                  <MapPin size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <div>Nema lokacija</div>
                </td></tr>
              )}
              {locations.map((l) => {
                const sm = STOCK_MODE_LABELS[l.stockMode] || STOCK_MODE_LABELS.strict;
                return (
                  <tr key={l.id}>
                    <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 12 }}>{l.tenantName || '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>{l.name}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{l.city || '-'}</td>
                    <td style={td}>
                      <span style={{ ...badge, background: l.posMode === 'restaurant' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)', color: l.posMode === 'restaurant' ? '#fbbf24' : '#60a5fa' }}>
                        {l.posMode === 'restaurant' ? 'Restoran' : 'Prodavnica'}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ ...badge, background: `${sm.color}22`, color: sm.color }}>{sm.label.split(' ')[0]}</span>
                    </td>
                    <td style={td}>
                      {l.hasCertificate ? <Check size={14} color="#10b981" /> : <AlertTriangle size={14} color="#f59e0b" />}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{l.salesCount ?? 0}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text-muted)' }}>{l.movementCount ?? 0}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {l.posMode === 'restaurant' && (
                        <>
                          <Link to={`/locations/${l.id}/layout`} style={{ ...iconBtn, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} title="Layout (zone i stolovi)">
                            <Layout size={13} />
                          </Link>
                          <Link to={`/locations/${l.id}/kitchen`} style={{ ...iconBtn, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} title="Kuhinja (stations + routes)">
                            <ChefHat size={13} />
                          </Link>
                          <Link to={`/locations/${l.id}/menu`} style={{ ...iconBtn, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} title="Dnevni meniji + 86'd">
                            <BookOpen size={13} />
                          </Link>
                          <Link to={`/locations/${l.id}/reservations`} style={{ ...iconBtn, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} title="Rezervacije">
                            <Calendar size={13} />
                          </Link>
                          <Link to={`/locations/${l.id}/starters`} style={{ ...iconBtn, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} title="Starters (auto-gratis)">
                            <Gift size={13} />
                          </Link>
                        </>
                      )}
                      <button onClick={() => setEditing(l)} style={iconBtn} title="Izmeni">
                        <Edit size={13} />
                      </button>
                      <button onClick={() => setDeleteTarget(l)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <LocationFormDialog
          location={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Obriši lokaciju"
          message={`Da li ste sigurni da želite da obrišete lokaciju "${deleteTarget.name}"? Ova akcija se ne može opozvati.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      <div style={{ marginTop: 16, padding: 12, background: 'var(--color-bg-surface)', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-muted)' }}>
        <Shield size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        <strong style={{ color: 'var(--color-text)' }}>Stock mod</strong> kontroliše da li sistem dozvoljava prodaju u minus za praćene proizvode. <strong style={{ color: '#10b981' }}>Strict</strong> blokira (preporuka za maloprodaju), <strong style={{ color: '#f59e0b' }}>warn</strong> dozvoljava ali logira, <strong style={{ color: '#6b7280' }}>lenient</strong> uvek dozvoljava (preporuka za restoran).
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '12px 14px', fontSize: 11, fontWeight: 600,
  color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
  textAlign: 'left', borderBottom: '1px solid var(--color-border)',
};

const td: React.CSSProperties = {
  padding: '12px 14px', fontSize: 13, color: 'var(--color-text)',
  borderBottom: '1px solid var(--color-border)',
};

const badge: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 12,
  fontSize: 11, fontWeight: 600,
};

const btnPrimary: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 6, border: 'none',
  background: 'var(--color-primary)', color: '#fff',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none',
  color: 'var(--color-text-muted)', padding: 6, marginLeft: 4,
  borderRadius: 4, cursor: 'pointer',
};

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 14px', borderRadius: 6,
  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
  fontSize: 13, marginBottom: 16,
};
